import React, { useState } from 'react';
import './RestoreBackupModal.css';

const RestoreBackupModal = ({ backup, districts, onRestore, onClose }) => {
  const [confirmText, setConfirmText] = useState('');
  const [targetDistrict, setTargetDistrict] = useState('');
  const [restoring, setRestoring] = useState(false);

  const isFullBackup = backup.backup_type === 'full';
  const requiresConfirmation = isFullBackup;
  const confirmationText = 'RESTORE';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (requiresConfirmation && confirmText !== confirmationText) {
      alert(`Please type "${confirmationText}" to confirm`);
      return;
    }

    if (!isFullBackup && targetDistrict === '') {
      alert('Please select a target district or choose to create a new district');
      return;
    }

    setRestoring(true);
    try {
      await onRestore(backup, true, targetDistrict === 'new' ? null : targetDistrict || null);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content restore-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Restore Backup</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="restore-warning">
              <h4>⚠️ Warning: This action cannot be undone!</h4>
              <p>
                {isFullBackup
                  ? 'Restoring this backup will overwrite the entire database with the backup data.'
                  : `Restoring this backup will overwrite all data for the selected district.`}
              </p>
            </div>

            <div className="backup-details">
              <p><strong>Backup Type:</strong> {backup.backup_type === 'full' ? 'Full Database' : 'District'}</p>
              <p><strong>Filename:</strong> <code>{backup.filename}</code></p>
              {backup.district_subdomain && (
                <p><strong>Original District:</strong> {backup.district_subdomain}</p>
              )}
              {backup.record_counts && (
                <div className="record-counts">
                  <strong>Record Counts:</strong>
                  <ul>
                    {Object.entries(backup.record_counts).map(([key, value]) => (
                      <li key={key}>{key}: {value}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {!isFullBackup && (
              <div className="form-group">
                <label htmlFor="target-district">
                  Restore to:
                </label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="restore-target"
                      value="new"
                      checked={targetDistrict === 'new'}
                      onChange={() => setTargetDistrict('new')}
                    />
                    Create new district
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="restore-target"
                      value="existing"
                      checked={targetDistrict !== 'new' && targetDistrict !== ''}
                      onChange={() => setTargetDistrict('')}
                    />
                    Existing district
                  </label>
                </div>
                {targetDistrict !== 'new' && (
                  <select
                    id="target-district"
                    value={targetDistrict}
                    onChange={(e) => setTargetDistrict(e.target.value)}
                    required={targetDistrict !== 'new'}
                  >
                    <option value="">-- Select District --</option>
                    {districts.map((district) => (
                      <option key={district.id} value={district.id}>
                        {district.district_name} ({district.subdomain})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {requiresConfirmation && (
              <div className="form-group">
                <label htmlFor="confirm-text">
                  Type <strong>{confirmationText}</strong> to confirm:
                </label>
                <input
                  type="text"
                  id="confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={confirmationText}
                  required
                />
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={restoring}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-danger"
              disabled={restoring || (requiresConfirmation && confirmText !== confirmationText)}
            >
              {restoring ? 'Restoring...' : 'Restore Backup'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RestoreBackupModal;
